import { useState } from "react";
import { Boxes, Download, LogOut, ChevronDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Header({ runId }) {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState(false);

  const doExport = (format, scope) => async () => {
    if (!runId) { toast.error("No hay una corrida activa para exportar"); return; }
    setBusy(true);
    try {
      await downloadFile(`/recon/runs/${runId}/export?format=${format}&scope=${scope}`, `conciliacion.${format}`);
      toast.success("Exportación generada");
    } catch {
      toast.error("No se pudo exportar");
    } finally {
      setBusy(false);
    }
  };

  const initials = (user?.name || user?.email || "?").trim().slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0]" data-testid="app-header">
      <div className="px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-sm bg-[#0F172A] flex items-center justify-center text-white shrink-0">
            <Boxes size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold tracking-tight text-slate-900 leading-tight truncate">
              Conciliación de Deliveries
            </div>
            <div className="text-[11px] text-slate-400 leading-tight font-mono truncate">Reporte STORIS · Logistical Scheduling</div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-menu-button" disabled={busy}
                className="rounded-sm border-slate-300 h-9 gap-2">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                <span className="hidden sm:inline">Exportar</span>
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-56">
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-500">Todos los resultados</DropdownMenuLabel>
              <DropdownMenuItem data-testid="export-all-xlsx" onClick={doExport("xlsx", "all")}><FileSpreadsheet size={15} /> Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-all-csv" onClick={doExport("csv", "all")}><FileText size={15} /> CSV (.csv)</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-500">Solo excepciones</DropdownMenuLabel>
              <DropdownMenuItem data-testid="export-exc-xlsx" onClick={doExport("xlsx", "exceptions")}><FileSpreadsheet size={15} /> Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-exc-csv" onClick={doExport("csv", "exceptions")}><FileText size={15} /> CSV (.csv)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="h-9 w-9 rounded-sm bg-[#0F172A] text-white text-xs font-semibold flex items-center justify-center">
                {initials}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-56">
              <DropdownMenuLabel className="truncate">
                <div className="font-medium text-slate-900">{user?.name}</div>
                <div className="text-xs text-slate-400 font-mono truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-[#991B1B]">
                <LogOut size={15} /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
